/**
 * CourseWorkspace — full-page admin editor for a single course.
 *
 * Inspired by the learner-facing course layout: sticky left sidebar
 * with the module + lesson tree, and a main pane on the right that
 * shows the correct editor (course settings / module / lesson) based
 * on sidebar selection. Replaces the old "expand a card, scroll to
 * the top" flow that made it easy for admins to lose their place in
 * a long course.
 *
 * Route: /admin/lms/course/:courseId/workspace
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ChevronDown, ChevronRight, Plus, Settings,
  FileText, Layers, Loader2, Home, Sparkles, Eye, EyeOff, Save, GripVertical,
} from 'lucide-react';
import CourseForm from '../../components/admin/lms/CourseForm';
import ModuleForm from '../../components/admin/lms/ModuleForm';
import LessonForm from '../../components/admin/lms/LessonForm';
import RevisionMeta from '../../components/admin/lms/RevisionMeta';
import AutoCiteSidebar from '../../components/admin/lms/AutoCiteSidebar';
import { useLMSForms } from '../../hooks/lms/useLMSForms';
import { useContentSections } from '../../hooks/lms/useContentSections';
import { useQuizBuilder } from '../../hooks/lms/useQuizBuilder';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── Small sortable wrappers so we can reuse a single dnd context per
// list (modules at course level, lessons per module) ─────────────
function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-${id}`}>
      {typeof children === 'function' ? children({ attributes, listeners }) : children}
    </div>
  );
}

export default function CourseWorkspace() {
  const { courseId } = useParams();
  const navigate     = useNavigate();
  const { API }      = useContext(AuthContext);

  const [course, setCourse]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  // Sidebar search — filters modules + lessons by title. When a lesson
  // matches, its parent module is force-expanded and only the matching
  // lessons are shown inside it. Empty search = full tree.
  const [sidebarSearch, setSidebarSearch] = useState('');
  // selection drives which editor renders in the right pane.
  const [selection, setSelection] = useState({ type: 'course', id: courseId });
  // Auto-cite sidebar state — the last auto-cite response we haven't
  // dismissed. Populated by saveLesson right after the auto-cite call.
  const [autoCiteResult, setAutoCiteResult] = useState(null);

  const {
    courseForm, setCourseForm,
    moduleForm, setModuleForm,
    lessonForm, setLessonForm,
    editingCourse, setEditingCourse,
    editingModule, setEditingModule,
    editingLesson, setEditingLesson,
    moduleContentSectionForm, setModuleContentSectionForm,
    editingModuleSectionId, setEditingModuleSectionId,
  } = useLMSForms();

  const {
    contentSectionForm, setContentSectionForm,
    editingSectionId,
    expandedSections, setExpandedSections,
    addContentSection: addSectionHook,
    editContentSection,
    cancelEditSection,
    removeContentSection: removeSectionHook,
    moveContentSection: moveSectionHook,
  } = useContentSections();

  const {
    quizQuestion, setQuizQuestion, editingQuestionIndex,
    editQuizQuestion,
    updateQuizQuestion: updateQuizQuestionHook,
    cancelEditQuizQuestion,
    removeQuizQuestion: removeQuizQuestionHook,
  } = useQuizBuilder();

  const fetchCourse = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/lms/courses/${courseId}`);
      setCourse(data);
      // Expand the first module by default so the tree isn't empty on
      // first load.
      if (data?.modules?.[0]?.id) {
        setExpandedModules((prev) => (prev[data.modules[0].id] ? prev : { ...prev, [data.modules[0].id]: true }));
      }
    } catch {
      toast.error('Could not load course');
      setCourse(null);
    } finally {
      setLoading(false);
    }
  }, [API, courseId]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  // Seed the right-side form whenever the selection changes so the
  // correct data is preloaded.
  useEffect(() => {
    if (!course) return;
    if (selection.type === 'course') {
      setEditingCourse(course);
      setCourseForm({
        title:                course.title || '',
        description:          course.description || '',
        tier:                 course.tier || 'free',
        thumbnail_url:        course.thumbnail_url || '',
        instructor:           course.instructor || '',
        duration_minutes:     course.duration_minutes || '',
        order:                course.order || 0,
        is_published:         course.is_published !== false,
        visibility:           course.visibility || 'public',
        access_code_required: !!course.access_code_required,
        themes:               course.themes || [],
        ad_enabled:           course.ad_enabled || false,
        ad_title:             course.ad_title || '',
        ad_content:           course.ad_content || '',
        ad_image_url:         course.ad_image_url || '',
        ad_link_url:          course.ad_link_url || '',
        ad_link_text:         course.ad_link_text || '',
      });
    } else if (selection.type === 'module') {
      const m = course.modules?.find((x) => x.id === selection.id);
      if (m) {
        setEditingModule(m);
        setModuleForm({
          course_id:        course.id,
          title:            m.title || '',
          description:      m.description || '',
          content_sections: m.content_sections || [],
          order:            m.order || 0,
          support_photos:   m.support_photos || [],
          support_videos:   m.support_videos || [],
          support_links:    m.support_links || [],
          support_downloads: m.support_downloads || [],
          ad_enabled:       m.ad_enabled || false,
          ad_title:         m.ad_title || '',
          ad_content:       m.ad_content || '',
          ad_image_url:     m.ad_image_url || '',
          ad_link_url:      m.ad_link_url || '',
          ad_link_text:     m.ad_link_text || '',
        });
      }
    } else if (selection.type === 'lesson') {
      let target = null; let parentModule = null;
      for (const m of course.modules || []) {
        const l = m.lessons?.find((x) => x.id === selection.id);
        if (l) { target = l; parentModule = m; break; }
      }
      if (target) {
        setEditingLesson(target);
        setLessonForm({
          module_id:        parentModule.id,
          title:            target.title || '',
          description:      target.description || '',
          content_sections: target.content_sections || [],
          content_type:     target.content_type || 'video',
          video_url:        target.video_url || '',
          written_content:  target.written_content || '',
          pdf_url:          target.pdf_url || '',
          markdown_content: target.markdown_content || '',
          duration_minutes: target.duration_minutes || '',
          order:            target.order || 0,
          support_photos:   target.support_photos || [],
          support_videos:   target.support_videos || [],
          support_links:    target.support_links || [],
          support_downloads: target.support_downloads || [],
          ad_enabled:       target.ad_enabled || false,
          ad_title:         target.ad_title || '',
          ad_content:       target.ad_content || '',
          ad_image_url:     target.ad_image_url || '',
          ad_link_url:      target.ad_link_url || '',
          ad_link_text:     target.ad_link_text || '',
          ad_position:      target.ad_position || 'bottom',
        });
      }
    } else if (selection.type === 'new-module') {
      setEditingModule(null);
      setModuleForm({
        course_id: course.id, title: '', description: '', content_sections: [],
        order: (course.modules?.length || 0), support_photos: [], support_videos: [],
        support_links: [], support_downloads: [],
        ad_enabled: false, ad_title: '', ad_content: '', ad_image_url: '',
        ad_link_url: '', ad_link_text: '',
      });
    } else if (selection.type === 'new-lesson') {
      setEditingLesson(null);
      const m = course.modules?.find((x) => x.id === selection.id);
      setLessonForm({
        module_id: selection.id, title: '', description: '', content_sections: [],
        content_type: 'video', video_url: '', written_content: '', pdf_url: '',
        markdown_content: '', duration_minutes: '', order: (m?.lessons?.length || 0),
        support_photos: [], support_videos: [], support_links: [], support_downloads: [],
        ad_enabled: false, ad_title: '', ad_content: '', ad_image_url: '',
        ad_link_url: '', ad_link_text: '', ad_position: 'bottom',
      });
    }
    // If selection is a "new-*" intent or unrecognized, no-op.
  }, [selection, course]);

  // ── CRUD (uses the same endpoints LMSAdmin uses) ──
  const saveCourse = async () => {
    setSaving(true);
    try {
      const data = {
        ...courseForm,
        duration_minutes: courseForm.duration_minutes ? parseInt(courseForm.duration_minutes, 10) : null,
      };
      await axios.put(`${API}/lms/courses/${courseId}`, data);
      toast.success('Course updated');
      await fetchCourse();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save course'); }
    finally { setSaving(false); }
  };

  const saveModule = async () => {
    if (!moduleForm.title) { toast.error('Module needs a title.'); return; }
    setSaving(true);
    try {
      if (editingModule?.id) {
        await axios.put(`${API}/lms/modules/${editingModule.id}`, moduleForm);
        toast.success('Module updated');
      } else {
        const { data } = await axios.post(`${API}/lms/modules`, moduleForm);
        toast.success('Module created');
        if (data?.id) setSelection({ type: 'module', id: data.id });
      }
      await fetchCourse();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save module'); }
    finally { setSaving(false); }
  };

  const saveLesson = async () => {
    if (!lessonForm.title) { toast.error('Lesson needs a title.'); return; }
    setSaving(true);
    try {
      const data = {
        ...lessonForm,
        duration_minutes: lessonForm.duration_minutes ? parseInt(lessonForm.duration_minutes, 10) : null,
      };
      let savedLessonId = editingLesson?.id;
      if (editingLesson?.id) {
        await axios.put(`${API}/lms/lessons/${editingLesson.id}`, data);
        toast.success('Lesson updated');
      } else {
        const { data: created } = await axios.post(`${API}/lms/lessons`, data);
        toast.success('Lesson created');
        if (created?.id) {
          setSelection({ type: 'lesson', id: created.id });
          savedLessonId = created.id;
        }
      }
      // Fire auto-cite once the save persisted. Response is captured
      // into state so the Auto-cite Sidebar can list each source with
      // Accept / Reject buttons instead of the admin hunting `[cite:...]`
      // markers by hand.
      if (savedLessonId) {
        axios.post(`${API}/admin/lms/lessons/${savedLessonId}/auto-cite`)
          .then(({ data: r }) => {
            if (r?.added > 0) {
              setAutoCiteResult({ lessonId: savedLessonId, ...r });
              toast.success(
                `Auto-added ${r.added} citation${r.added === 1 ? '' : 's'}`,
                { duration: 4000, description: 'Review the sources in the right sidebar — accept or reject each.' }
              );
              fetchCourse();
            }
          })
          .catch(() => { /* silent — auto-cite is a nice-to-have, not a save blocker */ });
      }
      await fetchCourse();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save lesson'); }
    finally { setSaving(false); }
  };

  // ── Content-section handlers (bound to current lessonForm) ──
  // Mirror LMSAdmin's wrappers so the LessonForm behaves identically here.
  const addContentSection = () => {
    if (!contentSectionForm.title) { toast.error('Please add a section title'); return; }
    if (contentSectionForm.type === 'quiz' && (!contentSectionForm.quiz_data?.questions || contentSectionForm.quiz_data.questions.length === 0)) {
      toast.error('Please add at least one question to the quiz');
      return;
    }
    const updated = addSectionHook(lessonForm.content_sections || []);
    if (updated) setLessonForm({ ...lessonForm, content_sections: updated });
  };
  const removeContentSection = (sectionId) => {
    const updated = removeSectionHook(sectionId, lessonForm.content_sections || []);
    setLessonForm({ ...lessonForm, content_sections: updated });
  };
  const moveContentSection = (index, direction) => {
    const updated = moveSectionHook(index, direction, lessonForm.content_sections || []);
    setLessonForm({ ...lessonForm, content_sections: updated });
  };

  // Module-level content section (uses moduleContentSectionForm)
  const addModuleContentSection = () => {
    if (!moduleContentSectionForm.title) { toast.error('Please add a section title'); return; }
    if (editingModuleSectionId) {
      const updated = (moduleForm.content_sections || []).map((s) =>
        s.id === editingModuleSectionId ? { ...s, ...moduleContentSectionForm } : s,
      );
      setModuleForm({ ...moduleForm, content_sections: updated });
      setEditingModuleSectionId(null);
      toast.success('Content section updated');
    } else {
      const newSection = { id: Date.now().toString(), order: (moduleForm.content_sections || []).length, ...moduleContentSectionForm };
      setModuleForm({ ...moduleForm, content_sections: [...(moduleForm.content_sections || []), newSection] });
      toast.success('Content section added');
    }
    setModuleContentSectionForm({
      title: '', type: 'video', order: 0, video_url: '', text_content: '',
      markdown_content: '', pdf_url: '', presentation_url: '',
    });
  };
  const editModuleContentSection = (section) => {
    setModuleContentSectionForm({
      title: section.title || '', type: section.type || 'video',
      order: section.order || 0, video_url: section.video_url || '',
      text_content: section.text_content || '', markdown_content: section.markdown_content || '',
      pdf_url: section.pdf_url || '', presentation_url: section.presentation_url || '',
    });
    setEditingModuleSectionId(section.id);
  };
  const removeModuleContentSection = (sectionId) => {
    setModuleForm({ ...moduleForm, content_sections: (moduleForm.content_sections || []).filter((s) => s.id !== sectionId) });
    if (editingModuleSectionId === sectionId) setEditingModuleSectionId(null);
  };
  const cancelEditModuleSection = () => {
    setEditingModuleSectionId(null);
    setModuleContentSectionForm({
      title: '', type: 'video', order: 0, video_url: '', text_content: '',
      markdown_content: '', pdf_url: '', presentation_url: '',
    });
  };

  // Quiz question handlers (bound to contentSectionForm's quiz_data)
  const addQuizQuestion = () => {
    if (!quizQuestion.question || !quizQuestion.correct_answer) {
      toast.error('Please fill question and correct answer');
      return;
    }
    setContentSectionForm({
      ...contentSectionForm,
      quiz_data: {
        ...contentSectionForm.quiz_data,
        questions: [...(contentSectionForm.quiz_data?.questions || []), { ...quizQuestion }],
      },
    });
    setQuizQuestion({ question: '', type: 'multiple_choice', options: ['', '', '', ''], correct_answer: '', explanation: '' });
  };
  const updateQuizQuestion = () => {
    const updated = updateQuizQuestionHook(contentSectionForm.quiz_data?.questions || []);
    if (updated) setContentSectionForm({ ...contentSectionForm, quiz_data: { ...contentSectionForm.quiz_data, questions: updated } });
  };
  const removeQuizQuestion = (index) => {
    const updated = removeQuizQuestionHook(index, contentSectionForm.quiz_data?.questions || []);
    if (updated) setContentSectionForm({ ...contentSectionForm, quiz_data: { ...contentSectionForm.quiz_data, questions: updated } });
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its lessons?')) return;
    try {
      await axios.delete(`${API}/lms/modules/${moduleId}`);
      toast.success('Module deleted');
      setSelection({ type: 'course', id: courseId });
      await fetchCourse();
    } catch { toast.error('Failed to delete module'); }
  };
  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;
    try {
      await axios.delete(`${API}/lms/lessons/${lessonId}`);
      toast.success('Lesson deleted');
      setSelection({ type: 'course', id: courseId });
      await fetchCourse();
    } catch { toast.error('Failed to delete lesson'); }
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  // Build the filtered module tree from sidebarSearch. Modules whose
  // title matches are kept intact; modules with matching lessons are
  // kept but pruned to only those matches (and force-expanded).
  const filteredTree = useMemo(() => {
    const q = sidebarSearch.trim().toLowerCase();
    if (!course?.modules) return { modules: [], forceExpanded: {} };
    if (!q) return { modules: course.modules, forceExpanded: {} };
    const forceExpanded = {};
    const modules = course.modules
      .map((m) => {
        const mHit = (m.title || '').toLowerCase().includes(q);
        const matchingLessons = (m.lessons || []).filter((l) => (l.title || '').toLowerCase().includes(q));
        if (mHit) return m; // keep full module
        if (matchingLessons.length > 0) {
          forceExpanded[m.id] = true;
          return { ...m, lessons: matchingLessons };
        }
        return null;
      })
      .filter(Boolean);
    return { modules, forceExpanded };
  }, [course, sidebarSearch]);

  // ── Drag-sort + publish + template handlers ──
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleModulesDragEnd = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id || !course) return;
    const ids = (course.modules || []).map((m) => m.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newIds = arrayMove(ids, oldIdx, newIdx);
    // Optimistic UI — reflow the sidebar immediately.
    setCourse((prev) => ({
      ...prev,
      modules: newIds.map((id) => prev.modules.find((m) => m.id === id)),
    }));
    try {
      await axios.post(`${API}/lms/modules/reorder`, {
        course_id: course.id,
        module_ids: newIds,
      });
    } catch {
      toast.error('Could not save module order');
      await fetchCourse();
    }
  };

  const handleLessonsDragEnd = (moduleId) => async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id || !course) return;
    const module = course.modules?.find((m) => m.id === moduleId);
    if (!module) return;
    const ids = (module.lessons || []).map((l) => l.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newIds = arrayMove(ids, oldIdx, newIdx);
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.map((m) => m.id === moduleId
        ? { ...m, lessons: newIds.map((id) => m.lessons.find((l) => l.id === id)) }
        : m),
    }));
    try {
      await axios.post(`${API}/lms/lessons/reorder`, {
        module_id: moduleId,
        lesson_ids: newIds,
      });
    } catch {
      toast.error('Could not save lesson order');
      await fetchCourse();
    }
  };

  const togglePublish = async (kind /* 'module' | 'lesson' */, id, currentValue) => {
    const nextValue = !currentValue;
    // Optimistic — flip in local state right away so the sidebar
    // pill updates without waiting on the network.
    setCourse((prev) => {
      if (!prev) return prev;
      if (kind === 'module') {
        return { ...prev, modules: prev.modules.map((m) => m.id === id ? { ...m, is_published: nextValue } : m) };
      }
      return {
        ...prev,
        modules: prev.modules.map((m) => ({
          ...m,
          lessons: (m.lessons || []).map((l) => l.id === id ? { ...l, is_published: nextValue } : l),
        })),
      };
    });
    try {
      await axios.patch(`${API}/lms/${kind}s/${id}/publish`, { is_published: nextValue });
    } catch {
      toast.error(`Could not update ${kind} publish state`);
      await fetchCourse();
    }
  };

  const saveAsTemplate = async () => {
    if (!course) return;
    const name = window.prompt('Template name?', `Template — ${course.title}`);
    if (!name) return;
    try {
      await axios.post(`${API}/admin/lms/templates`, {
        course_id: course.id, name,
      });
      toast.success('Saved as template');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save template');
    }
  };

  // Breadcrumb built from current selection
  const breadcrumb = useMemo(() => {
    if (!course) return [];
    const trail = [{ label: course.title, onClick: () => setSelection({ type: 'course', id: courseId }) }];
    if (selection.type === 'module' || selection.type === 'new-lesson') {
      const m = course.modules?.find((x) => x.id === selection.id);
      if (m) trail.push({ label: m.title, onClick: () => setSelection({ type: 'module', id: m.id }) });
    } else if (selection.type === 'lesson') {
      for (const m of course.modules || []) {
        const l = m.lessons?.find((x) => x.id === selection.id);
        if (l) {
          trail.push({ label: m.title, onClick: () => setSelection({ type: 'module', id: m.id }) });
          trail.push({ label: l.title, onClick: () => {} });
          break;
        }
      }
    } else if (selection.type === 'new-module') {
      trail.push({ label: 'New module', onClick: () => {} });
    }
    return trail;
  }, [course, selection, courseId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!course) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <p>Course not found.</p>
        <Button onClick={() => navigate('/admin/lms')}>Back to LMS Admin</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="course-workspace">
      {/* Header */}
      <div className="sticky top-0 z-30 border-b border-border bg-white/95 dark:bg-slate-900/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto pl-32 pr-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/lms')} data-testid="workspace-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> All courses
          </Button>
          <div className="h-4 w-px bg-slate-300 dark:bg-slate-700" />
          <nav className="flex items-center gap-1 text-sm min-w-0 flex-1" data-testid="workspace-breadcrumb">
            <Home className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            {breadcrumb.map((b, i) => (
              <React.Fragment key={i}>
                <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />
                <button
                  type="button"
                  onClick={b.onClick}
                  className={`truncate hover:underline text-left ${i === breadcrumb.length - 1 ? 'font-semibold text-foreground' : 'text-slate-500'}`}
                  title={b.label}
                >
                  {b.label}
                </button>
              </React.Fragment>
            ))}
          </nav>
          <div className="flex items-center gap-1.5 shrink-0">
            <RevisionMeta
              size="xs"
              createdAt={course.created_at}
              publishedAt={course.published_at}
              reviewBy={course.review_by}
              testIdPrefix="workspace-course-revision"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={saveAsTemplate}
              title="Save this course's module + lesson skeleton as a reusable template"
              data-testid="workspace-save-template"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> Save as template
            </Button>
            {!course.is_published && <span className="text-[10px] px-2 py-0.5 rounded bg-red-100 text-red-800 font-semibold">DRAFT</span>}
            {course.visibility === 'private' && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-semibold">PRIVATE</span>}
          </div>
        </div>
      </div>

      {/* Body — two columns */}
      <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row gap-4 px-4 py-4">
        {/* Sidebar */}
        <aside
          className="md:w-80 shrink-0 md:sticky md:top-[72px] md:self-start md:max-h-[calc(100vh-88px)] md:overflow-y-auto"
          data-testid="workspace-sidebar"
        >
          <Card>
            <CardContent className="p-3">
              <div className="relative mb-2">
                <input
                  type="text"
                  value={sidebarSearch}
                  onChange={(e) => setSidebarSearch(e.target.value)}
                  placeholder="Search modules & lessons…"
                  className="w-full h-8 pl-2 pr-7 rounded-md border border-input bg-background text-xs"
                  data-testid="ws-sidebar-search"
                />
                {sidebarSearch && (
                  <button
                    type="button"
                    onClick={() => setSidebarSearch('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600 px-1"
                    title="Clear search"
                    data-testid="ws-sidebar-search-clear"
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelection({ type: 'course', id: courseId })}
                className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm mb-2 transition-colors ${
                  selection.type === 'course'
                    ? 'bg-primary/10 text-primary font-semibold border border-primary/30'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
                data-testid="ws-select-course"
              >
                <Settings className="w-4 h-4 shrink-0" />
                <span className="truncate flex-1 text-left">Course settings</span>
              </button>

              <div className="text-[10px] uppercase tracking-widest text-slate-500 px-2 mt-2 mb-1 flex items-center justify-between">
                <span>Curriculum</span>
                <span>
                  {sidebarSearch
                    ? `${filteredTree.modules.length} match${filteredTree.modules.length === 1 ? '' : 'es'}`
                    : `${course.modules?.length || 0} modules`}
                </span>
              </div>

              {sidebarSearch && filteredTree.modules.length === 0 && (
                <div className="text-xs text-slate-500 italic px-2 py-3 text-center" data-testid="ws-sidebar-no-match">
                  No matches for &ldquo;{sidebarSearch}&rdquo;
                </div>
              )}

              {(filteredTree.modules || []).length > 0 && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModulesDragEnd}>
                  <SortableContext items={(filteredTree.modules || []).map((m) => m.id)} strategy={verticalListSortingStrategy}>
                    {(filteredTree.modules || []).map((m) => {
                      const isSelected = selection.type === 'module' && selection.id === m.id;
                      const isExpanded = !!expandedModules[m.id] || !!filteredTree.forceExpanded[m.id];
                      return (
                        <SortableRow key={m.id} id={m.id}>
                          {({ attributes, listeners }) => (
                            <div className="mb-1">
                              <div className={`group flex items-stretch rounded-md overflow-hidden ${isSelected ? 'ring-1 ring-primary/40' : ''}`}>
                                <button
                                  type="button"
                                  {...attributes}
                                  {...listeners}
                                  className="px-1 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center cursor-grab active:cursor-grabbing"
                                  title="Drag to reorder"
                                  data-testid={`ws-drag-module-${m.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <GripVertical className="w-3 h-3 text-slate-400 group-hover:text-slate-600" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleModuleExpand(m.id)}
                                  className="px-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center"
                                  title={isExpanded ? 'Collapse' : 'Expand'}
                                  data-testid={`ws-toggle-module-${m.id}`}
                                >
                                  {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSelection({ type: 'module', id: m.id })}
                                  className={`flex-1 flex items-center gap-2 px-1 py-1.5 text-sm text-left transition-colors ${
                                    isSelected ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                                  }`}
                                  data-testid={`ws-select-module-${m.id}`}
                                >
                                  <Layers className="w-3.5 h-3.5 shrink-0" />
                                  <span className="truncate flex-1">{m.title}</span>
                                  {!m.is_published && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold shrink-0">D</span>}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); togglePublish('module', m.id, m.is_published); }}
                                  className={`px-1.5 flex items-center hover:bg-slate-100 dark:hover:bg-slate-800 ${
                                    m.is_published ? 'text-emerald-600' : 'text-slate-400'
                                  }`}
                                  title={m.is_published ? 'Published — click to unpublish' : 'Draft — click to publish'}
                                  data-testid={`ws-publish-module-${m.id}`}
                                >
                                  {m.is_published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                              {isExpanded && (
                                <div className="ml-4 mt-1 border-l border-slate-200 dark:border-slate-700 pl-2 space-y-0.5">
                                  <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleLessonsDragEnd(m.id)}
                                  >
                                    <SortableContext items={(m.lessons || []).map((l) => l.id)} strategy={verticalListSortingStrategy}>
                                      {(m.lessons || []).map((l) => {
                                        const lSelected = selection.type === 'lesson' && selection.id === l.id;
                                        return (
                                          <SortableRow key={l.id} id={l.id}>
                                            {({ attributes: la, listeners: ll }) => (
                                              <div className="group flex items-stretch rounded overflow-hidden">
                                                <button
                                                  type="button"
                                                  {...la}
                                                  {...ll}
                                                  className="px-1 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center cursor-grab active:cursor-grabbing"
                                                  title="Drag to reorder"
                                                  data-testid={`ws-drag-lesson-${l.id}`}
                                                >
                                                  <GripVertical className="w-3 h-3 text-slate-400 group-hover:text-slate-600" />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => setSelection({ type: 'lesson', id: l.id })}
                                                  className={`flex-1 flex items-center gap-2 px-2 py-1 text-xs text-left transition-colors ${
                                                    lSelected ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                                  }`}
                                                  data-testid={`ws-select-lesson-${l.id}`}
                                                >
                                                  <FileText className="w-3 h-3 shrink-0" />
                                                  <span className="truncate flex-1">{l.title}</span>
                                                  {!l.is_published && <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-800 font-semibold shrink-0">D</span>}
                                                  {l.content_generated === false && (
                                                    <Sparkles className="w-3 h-3 text-amber-500 shrink-0" title="Content pending — AI generator available on the right" />
                                                  )}
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={(e) => { e.stopPropagation(); togglePublish('lesson', l.id, l.is_published); }}
                                                  className={`px-1.5 flex items-center hover:bg-slate-100 dark:hover:bg-slate-800 ${
                                                    l.is_published ? 'text-emerald-600' : 'text-slate-400'
                                                  }`}
                                                  title={l.is_published ? 'Published — click to unpublish' : 'Draft — click to publish'}
                                                  data-testid={`ws-publish-lesson-${l.id}`}
                                                >
                                                  {l.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                                </button>
                                              </div>
                                            )}
                                          </SortableRow>
                                        );
                                      })}
                                    </SortableContext>
                                  </DndContext>
                                  <button
                                    type="button"
                                    onClick={() => setSelection({ type: 'new-lesson', id: m.id })}
                                    className="w-full flex items-center gap-2 px-2 py-1 rounded text-xs text-primary hover:bg-primary/10 transition-colors"
                                    data-testid={`ws-add-lesson-${m.id}`}
                                  >
                                    <Plus className="w-3 h-3" /> Add lesson
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </SortableRow>
                      );
                    })}
                  </SortableContext>
                </DndContext>
              )}

              <button
                type="button"
                onClick={() => setSelection({ type: 'new-module' })}
                className="w-full mt-2 flex items-center justify-center gap-2 px-2 py-2 rounded-md text-sm border border-dashed border-slate-300 dark:border-slate-600 text-slate-600 hover:border-primary hover:text-primary transition-colors"
                data-testid="ws-add-module"
              >
                <Plus className="w-4 h-4" /> Add module
              </button>
            </CardContent>
          </Card>
        </aside>

        {/* Main pane */}
        <main className="flex-1 min-w-0" data-testid="workspace-main">
          {selection.type === 'course' && (
            <CourseForm
              courseForm={courseForm}
              setCourseForm={setCourseForm}
              editingCourse={editingCourse}
              onSave={saveCourse}
              onCancel={() => navigate('/admin/lms')}
            />
          )}

          {(selection.type === 'module' || selection.type === 'new-module') && (
            <>
              {selection.type === 'module' && editingModule && (
                <RevisionMeta
                  className="mb-3"
                  createdAt={editingModule.created_at}
                  publishedAt={editingModule.published_at}
                  reviewBy={editingModule.review_by}
                  testIdPrefix="workspace-module-revision"
                />
              )}
              <ModuleForm
                moduleForm={moduleForm}
                setModuleForm={setModuleForm}
                editingModule={selection.type === 'module' ? editingModule : null}
                courses={[course]}
                moduleContentSectionForm={moduleContentSectionForm}
                setModuleContentSectionForm={setModuleContentSectionForm}
                editingModuleSectionId={editingModuleSectionId}
                onSave={saveModule}
                onCancel={() => setSelection({ type: 'course', id: courseId })}
                onAddContentSection={addModuleContentSection}
                onEditContentSection={editModuleContentSection}
                onRemoveContentSection={removeModuleContentSection}
                onCancelEditSection={cancelEditModuleSection}
              />
              {selection.type === 'module' && editingModule && (
                <div className="mt-3 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteModule(editingModule.id)}
                    className="text-red-600 hover:text-red-700"
                    data-testid="ws-delete-module"
                  >
                    Delete this module
                  </Button>
                </div>
              )}
            </>
          )}

          {(selection.type === 'lesson' || selection.type === 'new-lesson') && (
            <>
              {selection.type === 'lesson' && editingLesson && (
                <RevisionMeta
                  className="mb-3"
                  createdAt={editingLesson.created_at}
                  publishedAt={editingLesson.published_at}
                  reviewBy={editingLesson.review_by}
                  testIdPrefix="workspace-lesson-revision"
                />
              )}
              <LessonForm
                lessonForm={lessonForm}
                setLessonForm={setLessonForm}
                editingLesson={selection.type === 'lesson' ? editingLesson : null}
                courses={[course]}
                courseDetails={{ [course.id]: course }}
                contentSectionForm={contentSectionForm}
                setContentSectionForm={setContentSectionForm}
                editingSectionId={editingSectionId}
                quizQuestion={quizQuestion}
                setQuizQuestion={setQuizQuestion}
                editingQuestionIndex={editingQuestionIndex}
                expandedSections={expandedSections}
                setExpandedSections={setExpandedSections}
                onSave={saveLesson}
                onCancel={() => setSelection({ type: 'course', id: courseId })}
                onAddContentSection={addContentSection}
                onEditContentSection={editContentSection}
                onCancelEditSection={cancelEditSection}
                onRemoveContentSection={removeContentSection}
                onMoveContentSection={moveContentSection}
                onAddQuizQuestion={addQuizQuestion}
                onEditQuizQuestion={editQuizQuestion}
                onUpdateQuizQuestion={updateQuizQuestion}
                onCancelEditQuizQuestion={cancelEditQuizQuestion}
                onRemoveQuizQuestion={removeQuizQuestion}
              />
              {selection.type === 'lesson' && editingLesson && (
                <div className="mt-3 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteLesson(editingLesson.id)}
                    className="text-red-600 hover:text-red-700"
                    data-testid="ws-delete-lesson"
                  >
                    Delete this lesson
                  </Button>
                </div>
              )}
            </>
          )}

          {saving && (
            <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-md shadow-lg flex items-center gap-2 z-40">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
            </div>
          )}
        </main>
      </div>
      {/* Auto-cite result sidebar — only visible when a save just landed
          fresh citations. Dismiss X clears the state and the sidebar
          disappears. Reject also calls fetchCourse so the editor stays
          in sync with the stripped marker. */}
      <AutoCiteSidebar
        result={autoCiteResult}
        onClose={() => setAutoCiteResult(null)}
        onReloadCourse={fetchCourse}
      />
    </div>
  );
}
